import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useRateTablesList, useRateTableMutations } from "../api";
import { buildRateTableBatchActions, type RateTableTab } from "./batchActions";
import type { RateTable } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<RateTableTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useRateTableMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } = useRateTablesList({
    page: { number: pageNumber, size: pageSize },
    filter: { active: tab === "active" },
  });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<RateTable>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "currencyName",
      header: "Currency",
      cell: ({ row }) => row.original.currencyName,
    },
    {
      accessorKey: "accountingCode",
      header: "Accounting Code",
      cell: ({ row }) => row.original.accountingCode,
    },
    {
      accessorKey: "default",
      header: "Default?",
      cell: ({ row }) =>
        row.original.default ? <FontAwesomeIcon icon={faCheck} /> : "",
    },
  ];

  const runOnSelected = async (
    fn: (r: RateTable) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const r of targets) {
      try {
        await fn(r);
      } catch (err) {
        failures.push({ label: r.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} rate table(s). Default rate tables cannot be archived.`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (r) => archive(r.id),
        (n) => `${n} rate table(s) archived.`
      );
    } else {
      await runOnSelected(
        (r) => restore(r.id, r.name),
        (n) => `${n} rate table(s) restored.`
      );
    }
  };

  const batchActions = buildRateTableBatchActions(tab, sel.selectedIds.length, {
    onDelete: () => setConfirmOpen(true),
    onRestore: () => setConfirmOpen(true),
  });

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Rate Tables"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Rate Table"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as RateTableTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No rate tables found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active" ? "Archive rate tables?" : "Restore rate tables?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} rate table(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
